import { useMemo, useRef } from 'react';
import { withStyles, Tooltip, formattedCurrency, ClientAccountBalance } from 'vopay';
import { useTranslation } from 'react-i18next';
import styles from './useTableConfig.module.scss';
import Chip from './Chip/Chip';

const cn = withStyles(styles);

export function Labels({ labels }: { labels: string[] }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const ref = useRef<HTMLDivElement | null>(null);

  const overflowing = useMemo(() => {
    if (!ref.current) return false;

    return ref.current.scrollHeight > ref.current.clientHeight;
  }, [ref.current]);

  if (!labels.length) return <>-</>;

  return (
    <div className={cn('labelsContainer')}>
      <div ref={ref} className={cn('labels')}>
        {[...labels].map((label, idx) => (
          <Chip key={label + idx} label={label} className={cn('chip')} />
        ))}
      </div>

      {overflowing && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('allLabels', { defaultValue: 'All Labels' })}</h3>

              <ul>
                {[...labels].map((label, idx) => (
                  <li key={label + idx}>
                    <Chip label={label} className={cn('label')} />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

export function Limits({
  limit,
  balances,
  country,
}: {
  limit: string;
  balances?: ClientAccountBalance[];
  country: string;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const defaultCurrency = country === 'US' ? 'USD' : 'CAD';

  if (!balances) {
    return (
      <div>
        {defaultCurrency} {formattedCurrency(limit)}
      </div>
    );
  }

  return (
    <div className={cn('limitsCell')}>
      {balances.slice(0, 2).map((b) => (
        <div key={b.Currency || defaultCurrency}>
          {b.Currency || defaultCurrency} {formattedCurrency(limit.toString())}
        </div>
      ))}

      {balances.length > 2 && (
        <Tooltip
          hasButton={false}
          customElement={<div className={cn('viewAll')}>{t('Tooltip.viewAll', { defaultValue: 'View All' })}</div>}
          information={
            <div className={cn('labelsTooltip')}>
              <h3>{t('Tooltip.transactionLimits', { defaultValue: 'Transaction Limits' })}</h3>

              <ul>
                {balances.map((b, idx) => (
                  <li key={(b.Currency || defaultCurrency) + idx}>
                    <Chip
                      label={`${b.Currency || defaultCurrency} ${formattedCurrency(limit)}`}
                      className={cn('label')}
                    />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

// One of the Available / Pending / Total balance columns: an amount per currency, one per line. Every
// currency is listed, in the same order in all three columns, so each currency's amounts line up
// across the row.
export function BalanceAmounts({
  balances,
  country,
  amount,
}: {
  balances?: ClientAccountBalance[];
  country: string;
  amount: 'AvailableBalance' | 'PendingBalance' | 'Balance';
}) {
  const defaultCurrency = country === 'US' ? 'USD' : 'CAD';

  if (!balances) return <>-</>;

  return (
    <div className={cn('balanceAmounts')}>
      {balances.map((b, idx) => (
        <div key={(b.Currency || defaultCurrency) + idx}>
          {b.Currency || defaultCurrency} {formattedCurrency(String(b[amount]))}
        </div>
      ))}
    </div>
  );
}
